export type UploadItemState =
  | 'checking'
  | 'ready'
  | 'uploading'
  | 'complete'
  | 'cancelled'
  | 'failed';

export interface UploadProjection {
  usedBytes: number;
  declaredBytes: number;
  projectedBytes: number;
  ceilingBytes: number;
}

export interface UploadProgressProps {
  filename: string;
  assetTitle: string;
  state: UploadItemState;
  progress: number;
  projection?: UploadProjection;
  error?: string;
  onCancel?: () => void;
  onRetry?: () => void;
}

function formatBytes(bytes: number): string {
  if (bytes >= 1_000_000_000) return `${(bytes / 1_000_000_000).toFixed(2)} GB`;
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  if (bytes >= 1_000) return `${(bytes / 1_000).toFixed(1)} KB`;
  return `${bytes} bytes`;
}

const stateLabels: Record<UploadItemState, string> = {
  checking: 'Checking storage',
  ready: 'Starting upload',
  uploading: 'Uploading',
  complete: 'Uploaded',
  cancelled: 'Cancelled',
  failed: 'Needs attention',
};

const stateClasses: Record<UploadItemState, string> = {
  checking: 'bg-elevated text-muted',
  ready: 'bg-accent-tint text-accent',
  uploading: 'bg-accent-tint text-accent',
  complete: 'bg-success-tint text-success',
  cancelled: 'bg-warning-tint text-warning',
  failed: 'bg-error-tint text-error',
};

export function UploadProgress({
  filename,
  assetTitle,
  state,
  progress,
  projection,
  error,
  onCancel,
  onRetry,
}: UploadProgressProps) {
  return (
    <article className="rounded-card border border-line bg-card p-4 shadow-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-fg">{assetTitle}</p>
          <p className="truncate text-sm text-faint">{filename}</p>
        </div>
        <span
          className={`inline-flex min-h-7 items-center rounded-full px-3 font-mono text-[0.7rem] font-semibold uppercase tracking-[0.12em] ${stateClasses[state]}`}
        >
          {stateLabels[state]}
        </span>
      </div>

      {(state === 'uploading' || state === 'complete') && (
        <div className="mt-4">
          <div className="mb-1 flex justify-between text-sm text-muted">
            <span>{state === 'complete' ? 'Upload complete' : 'Uploading'}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div
            className="h-2 w-full overflow-hidden rounded-full bg-elevated-2"
            role="progressbar"
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${filename} upload progress`}
          >
            <div className="h-full rounded-full bg-accent" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      {projection && state !== 'complete' && (
        <p className="mt-3 text-sm text-muted">
          This file adds {formatBytes(projection.declaredBytes)}. Projected storage is{' '}
          {formatBytes(projection.projectedBytes)} of {formatBytes(projection.ceilingBytes)}.
        </p>
      )}

      {error && <p className="mt-3 rounded-input bg-error-tint px-3 py-2 text-sm text-error">{error}</p>}

      <div className="mt-4 flex flex-wrap gap-2">
        {(state === 'uploading' || state === 'ready' || state === 'checking') && onCancel && (
          <button
            className="inline-flex min-h-11 items-center rounded-full border border-accent px-4 text-sm font-semibold text-accent hover:bg-accent-tint"
            type="button"
            onClick={onCancel}
          >
            Cancel
          </button>
        )}
        {(state === 'failed' || state === 'cancelled') && onRetry && (
          <button
            className="inline-flex min-h-11 items-center rounded-full border border-accent px-4 text-sm font-semibold text-accent hover:bg-accent-tint"
            type="button"
            onClick={onRetry}
          >
            Try again
          </button>
        )}
      </div>
    </article>
  );
}
